import React, { useContext, useMemo } from "react";
import { SCHOOL_DATA_PATH } from "../../../functions/src/admin/paths";
import { langContext, termsUrl } from "../../api/Lang";
import { useDatabaseVal } from "../../api/firebaseHooks";
import { getVersionDisplay } from "../../api/version";
import { useStrings } from "../../common-strings";
import { useAppLayoutStrings } from "./appLayout-strings";
import { SchoolData } from "../../types";

type FooterProps = {
    onOpenPlatformSupport: () => void;
};

const footerLinkStyle: React.CSSProperties = {
    color: '#000000',
    fontSize: '12px',
    marginLeft: '10px',
};

export function Footer({ onOpenPlatformSupport }: FooterProps) {

    const [lang] = useContext(langContext);
    const [schoolData] = useDatabaseVal<SchoolData>(SCHOOL_DATA_PATH);
    const commonStrings = useStrings();
    const layoutStrings = useAppLayoutStrings();

    const canteenEmail = schoolData?.canteenEmail?.trim() || "";
    const canteenTel = schoolData?.canteenTel?.trim() || "";

    const footerContactBlock = useMemo(() => {
        if (!canteenEmail && !canteenTel) {
            return null;
        }
        return (
            <div className="footer-contact-details">
                {canteenEmail && (
                    <p className="footer-contact-line">
                        <span className="footer-contact-line__label">{layoutStrings.footerEmailLabel}</span>
                        <a href={`mailto:${canteenEmail}`}>{canteenEmail}</a>
                    </p>
                )}
                {canteenEmail && canteenTel && (
                    <span className="footer-contact-details__sep" aria-hidden="true">·</span>
                )}
                {canteenTel && (
                    <p className="footer-contact-line">
                        <span className="footer-contact-line__label">{layoutStrings.footerTelLabel}</span>
                        <a href={`tel:${canteenTel}`}>{canteenTel}</a>
                    </p>
                )}
            </div>
        );
    }, [canteenEmail, canteenTel, layoutStrings.footerEmailLabel, layoutStrings.footerTelLabel]);

    return <div className='footer'>
        <div className="footer-shell">
            <div className="footer-hours-title">
                <div className="footer-open-hours">{layoutStrings.openingHoursTitle}</div>
                <div className="footer-days">
                    {layoutStrings.mondayFriday}
                    <div>
                        {schoolData?.workingHours ?? '08:00 - 15:00'}
                    </div>
                </div>
                {footerContactBlock && (
                    <>
                        <div className="footer-separator" />
                        {footerContactBlock}
                    </>
                )}
            </div>
            <div className="footer-copyright">
                <div>
                    {layoutStrings.footerCopyright(new Date().getFullYear(), getVersionDisplay())}
                </div>
                <div>
                    <a href={termsUrl('terms', lang)} target="_blank" rel="noopener noreferrer" style={{ ...footerLinkStyle, marginLeft: 0 }}>
                        {layoutStrings.termsOfUse}
                    </a>
                    <a href={termsUrl('privacy', lang)} target="_blank" rel="noopener noreferrer" style={footerLinkStyle}>
                        {commonStrings.privacyPolicy}
                    </a>
                    <a href={termsUrl('cookies', lang)} target="_blank" rel="noopener noreferrer" style={footerLinkStyle}>
                        {commonStrings.cookiesPolicy}
                    </a>
                    <button
                        type="button"
                        className="footer-legal-link-button"
                        onClick={onOpenPlatformSupport}
                    >
                        {layoutStrings.platformSupportFooterLink}
                    </button>
                </div>
            </div>
        </div>
    </div>
}
